// PLYA shared basket helper.
// Adds lines through the Ajax Cart API and tells the theme about it with the same
// CartLinesUpdateEvent the theme's own product form uses, so the cart drawer opens
// and refreshes. Falls back to the cart page if anything goes wrong.
import { CartLinesUpdateEvent } from '@shopify/events';

const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

function cartSectionIds() {
  return [...document.querySelectorAll('cart-items-component')]
    .map((el) => el.dataset && el.dataset.sectionId)
    .filter(Boolean);
}

async function fetchCart() {
  const res = await fetch(root + 'cart.js', { headers: { Accept: 'application/json' } });
  return res.json();
}

/**
 * @param {HTMLElement} source element that dispatches the event
 * @param {{id:number|string, quantity:number}[]} items
 * @returns {Promise<{ok:boolean, message?:string}>}
 */
export async function plyaAdd(source, items) {
  const sections = cartSectionIds();
  const hasDrawer = !!document.querySelector('cart-drawer-component');
  const deferred = CartLinesUpdateEvent.createPromise();
  const itemCount = items.reduce((n, i) => n + (Number(i.quantity) || 0), 0);
  try {
    source.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: Number(i.quantity) })),
        promise: deferred.promise,
      })
    );
  } catch (e) {
    /* event is optional */
  }
  try {
    const body = { items: items.map((i) => ({ id: Number(i.id), quantity: Number(i.quantity) })) };
    if (sections.length) {
      body.sections = sections.join(',');
      body.sections_url = window.location.pathname;
    }
    const res = await fetch(root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json();
    const cart = await fetchCart();
    if (!res.ok || data.status) {
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { didError: true, items: cart.items, source: 'plya', itemCount },
      });
      return { ok: false, message: data.description || data.message || '' };
    }
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { didError: false, items: cart.items, source: 'plya', sourceId: source.id || 'plya', itemCount, sections: data.sections },
    });
    document.dispatchEvent(new CustomEvent('plya:cart-updated', { detail: { cart } }));
    if (!hasDrawer) window.location.href = root + 'cart';
    return { ok: true };
  } catch (e) {
    deferred.reject(e);
    window.location.href = root + 'cart';
    return { ok: false };
  }
}

export function plyaMoney(cents, format) {
  let fmt = format || '';
  if (!/\{\{/.test(fmt)) fmt = '£' + '{' + '{amount}' + '}';
  const v = cents / 100;
  return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (m, k) => {
    if (k === 'amount_no_decimals') return Math.round(v).toLocaleString('en-GB');
    if (k === 'amount_with_comma_separator') return v.toFixed(2).replace('.', ',');
    return v.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  });
}

/* ---------- Quick add on product cards and any [data-plya-add] button ---------- */
function cardMatch(card) {
  const data = card.querySelector('[data-plya-variants]');
  if (!data) return null;
  const variants = JSON.parse(data.textContent);
  const sel = [...card.querySelectorAll('[data-plya-opt]')].map((fs) => {
    const c = fs.querySelector('input:checked');
    return c ? c.value : null;
  });
  if (sel.includes(null)) return null;
  return variants.find((v) => v.o.every((x, i) => x === sel[i])) || null;
}

function cardUpdate(card) {
  const btn = card.querySelector('[data-plya-picker] [data-plya-add]');
  if (!btn) return;
  const v = cardMatch(card);
  const label = btn.querySelector('[data-plya-label]');
  const price = btn.querySelector('[data-plya-price]');
  const fmt = (window.Shopify && window.Shopify.money_format) || '';
  if (!v) {
    btn.disabled = true;
    if (label) label.textContent = 'Unavailable';
  } else if (!v.a) {
    btn.disabled = true;
    if (label) label.textContent = 'Sold out';
  } else {
    btn.disabled = false;
    if (label) label.textContent = 'Add to basket';
  }
  if (price && v) price.textContent = plyaMoney(v.p, fmt);
}

function flash(btn, text, ms) {
  const label = btn.querySelector('[data-plya-label]');
  if (!label) return;
  if (!btn.dataset.plyaText) btn.dataset.plyaText = label.textContent;
  label.textContent = text;
  if (ms) {
    setTimeout(() => {
      label.textContent = btn.dataset.plyaText;
      btn.classList.remove('is-done');
    }, ms);
  }
}

if (!window.__plyaBound) {
  window.__plyaBound = true;
  document.addEventListener('change', (e) => {
    const card = e.target.closest('[data-plya-picker]') && e.target.closest('[data-plya-card]');
    if (card) cardUpdate(card);
  });
  document.addEventListener('click', async (e) => {
    const open = e.target.closest('[data-plya-open]');
    if (open) {
      const card = open.closest('[data-plya-card]');
      card.classList.add('is-picking');
      open.setAttribute('aria-expanded', 'true');
      cardUpdate(card);
      const first = card.querySelector('[data-plya-picker] input:checked') || card.querySelector('[data-plya-picker] input');
      if (first) first.focus({ preventScroll: true });
      return;
    }
    const close = e.target.closest('[data-plya-close]');
    if (close) {
      const card = close.closest('[data-plya-card]');
      card.classList.remove('is-picking');
      const o = card.querySelector('[data-plya-open]');
      if (o) {
        o.setAttribute('aria-expanded', 'false');
        o.focus({ preventScroll: true });
      }
      return;
    }
    const btn = e.target.closest('[data-plya-add]');
    if (!btn || btn.disabled) return;
    e.preventDefault();
    let items = null;
    if (btn.dataset.items) {
      try { items = JSON.parse(btn.dataset.items); } catch (err) { items = null; }
    } else if (btn.dataset.variant) {
      items = [{ id: btn.dataset.variant, quantity: Number(btn.dataset.qty || 1) }];
    } else {
      const card = btn.closest('[data-plya-card]');
      const v = card && cardMatch(card);
      if (v && v.a) items = [{ id: v.id, quantity: 1 }];
    }
    if (!items || !items.length) return;
    const wrap = btn.closest('[data-plya-card]') || btn.parentElement;
    const msg = wrap && wrap.querySelector('[data-plya-msg]');
    if (msg) msg.hidden = true;
    btn.classList.add('is-loading');
    flash(btn, 'Adding…');
    const res = await plyaAdd(btn, items);
    btn.classList.remove('is-loading');
    if (res.ok) {
      btn.classList.add('is-done');
      flash(btn, 'Added ✓', 1800);
      const card = btn.closest('[data-plya-card]');
      if (card) setTimeout(() => card.classList.remove('is-picking'), 1200);
    } else {
      flash(btn, btn.dataset.plyaText || 'Add to basket', 1);
      if (msg) {
        msg.textContent = res.message || 'Sorry, that could not be added. Please try again.';
        msg.hidden = false;
      }
    }
  });
}

/* ---------- Desktop safety net ----------
   On wide screens the theme scrolls inside .page-wrapper and the window itself must stay at the top.
   If anything (a focused hidden input, an anchor jump) scrolls the window, the page looks blank,
   so put the window straight back. */
if (!window.__plyaScrollGuard) {
  window.__plyaScrollGuard = true;
  const wide = window.matchMedia('(min-width: 990px)');
  const fix = () => {
    if (!wide.matches) return;
    const wrap = document.querySelector('.page-wrapper');
    if (!wrap || getComputedStyle(wrap).overflowY === 'visible') return;
    if (window.scrollY || window.scrollX) window.scrollTo(0, 0);
  };
  window.addEventListener('scroll', fix, { passive: true });
  window.addEventListener('load', fix);
  fix();
}
